import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';
import { CartAddEvent } from '@theme/events';

class AppBundlePair extends Component {
  #selects = [];
  #products = {};
  #shades = [];
  #selectedVariants = [];
  #busy = false;
  #button;
  #error;
  #form;

  connectedCallback() {
    super.connectedCallback();
    this.#selects = Array.from(this.querySelectorAll('[data-bundle-select]'));
    this.#products = this.#readJson('[data-bundle-products]', {});
    this.#shades = this.#readJson('[data-bundle-shades]', []);
    this.#button = this.querySelector('button[name="add"]');
    this.#error = this.querySelector('[data-bundle-error]');
    this.#form = this.querySelector('form');
    for (const image of this.querySelectorAll('.bundle-builder__image')) {
      if (image instanceof HTMLImageElement) image.dataset.originalSrc = image.currentSrc || image.src;
    }
    const url = new URL(window.location.href);
    for (const select of this.#selects) {
      const group = select.dataset.shadeGroup;
      const variant = this.#products[group]?.find((v) => String(v.id) === url.searchParams.get(`${group}_variant`));
      select.value = variant?.options?.[Number(select.dataset.optionPosition) - 1] || '';
      select.addEventListener('change', this.#change);
    }
    this.#form?.addEventListener('submit', this.#submit);
    this.#change();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    for (const select of this.#selects) select.removeEventListener('change', this.#change);
    this.#form?.removeEventListener('submit', this.#submit);
  }

  #change = () => {
    for (const select of this.#selects) {
      const slot = select.closest('[data-bundle-slot]');
      const image = slot?.querySelector('.bundle-builder__image');
      const shade = this.#shades.find((s) => s.group === select.dataset.shadeGroup && s.name === select.value);
      if (image instanceof HTMLImageElement) {
        image.src = shade?.image || image.dataset.originalSrc;
        image.srcset = '';
        image.alt = select.value || select.dataset.optionName;
        image.classList.toggle('bundle-builder__image--placeholder', !shade?.image);
      }
      if (shade?.swatch && select.value) {
        select.style.backgroundColor = shade.swatch;
        select.style.borderColor = shade.swatch;
        select.style.color = shade.text || '#fff';
        slot?.style.setProperty('--bundle-selected-text-color', shade.text || '#fff');
      } else {
        select.style.removeProperty('background-color');
        select.style.removeProperty('border-color');
        select.style.removeProperty('color');
        slot?.style.removeProperty('--bundle-selected-text-color');
      }
    }
    this.#sync();
  };

  #sync() {
    this.#selectedVariants = [];
    if (!this.#selects.length || this.#selects.some((select) => !select.value)) {
      this.#button.disabled = true;
      this.#error?.classList.add('hidden');
      return;
    }
    for (const group of ['lip', 'stick']) {
      const choices = this.#selects.filter((s) => s.dataset.shadeGroup === group);
      const variants = this.#products[group];
      const variant = Array.isArray(variants) && variants.find((v) => {
        return choices.length === 2 && v.options?.length === 2 && choices.every((s) => {
          return v.options[Number(s.dataset.optionPosition) - 1] === s.value;
        });
      });
      if (!variant) {
        this.#button.disabled = true;
        this.#showError(this.dataset.unavailableMessage);
        return;
      }
      this.#selectedVariants.push(variant);
    }
    const fullPrice = this.#selectedVariants.reduce((sum, v) => sum + v.price, 0);
    const money = (value) => formatMoney(value, this.dataset.moneyFormat, this.dataset.currency);
    this.querySelector('[data-bundle-price]').textContent = money(Math.max(0, fullPrice - Number(this.dataset.discountCents)));
    const comparePrice = this.querySelector('[data-bundle-compare-price]');
    const compareMode = this.dataset.comparePriceMode || 'automatic';
    const customComparePrice = this.dataset.customComparePrice || '';
    comparePrice.textContent = compareMode === 'custom' ? customComparePrice : money(fullPrice);
    comparePrice.hidden = compareMode === 'none' || (compareMode === 'custom' && !customComparePrice.trim());
    const available = this.#selectedVariants.every((v) => v.available);
    this.#button.disabled = this.#busy || !available;
    if (available) this.#error?.classList.add('hidden');
    else this.#showError(this.dataset.soldOutMessage);
    const url = new URL(window.location.href);
    ['lip', 'stick'].forEach((group, i) => url.searchParams.set(`${group}_variant`, String(this.#selectedVariants[i].id)));
    history.replaceState({}, '', url.toString());
  }

  #submit = async (event) => {
    event.preventDefault();
    if (this.#busy || this.#button.disabled || this.#selectedVariants.length !== 2) return;
    this.#busy = true;
    this.#button.disabled = true;
    for (const select of this.#selects) select.disabled = true;
    this.#form.setAttribute('aria-busy', 'true');
    const sections = Array.from(document.querySelectorAll('cart-items-component'))
      .map((item) => item.dataset.sectionId).filter(Boolean);
    try {
      const response = await fetch(this.dataset.cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: this.#selectedVariants.map((v) => ({ id: v.id, quantity: 1 })),
          sections: [...new Set(sections)],
          sections_url: window.location.pathname,
        }),
      });
      const result = await response.json();
      if (!response.ok || result.status) {
        this.#showError(result.description || result.message || this.dataset.addError);
        this.dispatchEvent(new CartAddEvent(undefined, this.id, { didError: true, source: 'product-form-component' }));
        return;
      }
      let cart;
      try {
        const cartResponse = await fetch(this.dataset.cartUrl);
        if (cartResponse.ok) cart = await cartResponse.json();
      } catch {}
      this.#error?.classList.add('hidden');
      this.querySelector('[data-live-region]').textContent = Theme.translations.added;
      this.dispatchEvent(new CartAddEvent(cart, this.id, {
        source: 'product-form-component', itemCount: 2, sections: result.sections,
      }));
    } catch {
      this.#showError(this.dataset.addError);
    } finally {
      this.#busy = false;
      for (const select of this.#selects) select.disabled = false;
      this.#form.removeAttribute('aria-busy');
      this.#button.disabled = !this.#selectedVariants.every((v) => v.available);
    }
  };

  #showError(message) {
    if (!this.#error) return;
    this.#error.textContent = message;
    this.#error.classList.remove('hidden');
  }

  #readJson(selector, fallback) {
    try { return JSON.parse(this.querySelector(selector)?.textContent) || fallback; }
    catch { return fallback; }
  }
}

if (!customElements.get('app-bundle-pair')) customElements.define('app-bundle-pair', AppBundlePair);
